import DoctorCategory from "constants/type/user/DoctorCategory";

// Appointment status codes. Verbatim from bridge-frontend's
// navigo-module/helper/appointmentConstants.js — these are the server's values
// (api-appointment-service), so they must not be renumbered.
export const NAVIGO_STATUS = Object.freeze({
  SCHEDULED_LEGACY: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  CANCELLED: 3,
  CONFIRMED: 4,
  SCHEDULED: 5,
  RESCHEDULE_REQUESTED: 6,
  PHLEBO_ASSIGNED: 7,
  PICKUP: 8,
  SAMPLE_COLLECTED: 9,
  SAMPLE_RECEIVED: 10,
  REPORT_GENERATED: 11,
  SUCCESSFUL_HRA: 12,
  SUCCESSFUL_PRC: 13,
  DECLINED: 14,
  NOT_ELIGIBLE: 15,
  NOT_ANSWERED: 16,
  NOT_ANSWERED_3_ATTEMPTS: 17,
  CALL_LATER: 18,
  NOT_REACHABLE: 19,
  REQUEST_TO_LAB: 20,
  NO_SHOW: 21,
  LAB_VISITED: 22,
});

export const STATUS_LABEL = Object.freeze({
  [NAVIGO_STATUS.SCHEDULED_LEGACY]: "Scheduled",
  [NAVIGO_STATUS.IN_PROGRESS]: "In progress",
  [NAVIGO_STATUS.COMPLETED]: "Completed",
  [NAVIGO_STATUS.CANCELLED]: "Cancelled",
  [NAVIGO_STATUS.CONFIRMED]: "Confirmed",
  [NAVIGO_STATUS.SCHEDULED]: "Scheduled",
  [NAVIGO_STATUS.RESCHEDULE_REQUESTED]: "Reschedule requested",
  [NAVIGO_STATUS.PHLEBO_ASSIGNED]: "Phlebo assigned",
  [NAVIGO_STATUS.PICKUP]: "Pickup",
  [NAVIGO_STATUS.SAMPLE_COLLECTED]: "Sample collected",
  [NAVIGO_STATUS.SAMPLE_RECEIVED]: "Sample received",
  [NAVIGO_STATUS.REPORT_GENERATED]: "Report generated",
  [NAVIGO_STATUS.SUCCESSFUL_HRA]: "Successful HRA",
  [NAVIGO_STATUS.SUCCESSFUL_PRC]: "Successful PRC",
  [NAVIGO_STATUS.DECLINED]: "Declined",
  [NAVIGO_STATUS.NOT_ELIGIBLE]: "Not eligible",
  [NAVIGO_STATUS.NOT_ANSWERED]: "Not answered",
  [NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS]: "Not answered (3 attempts)",
  [NAVIGO_STATUS.CALL_LATER]: "Call later",
  [NAVIGO_STATUS.NOT_REACHABLE]: "Not reachable",
  [NAVIGO_STATUS.REQUEST_TO_LAB]: "Request to lab",
  [NAVIGO_STATUS.NO_SHOW]: "No show",
  [NAVIGO_STATUS.LAB_VISITED]: "Lab visited",
});

/**
 * The eight terminal statuses. Single source: END_STATUSES freezes this list,
 * and STATUS_TONE colours from it, so a status added here cannot end up
 * coloured as though it were still in flight.
 */
const TERMINAL_STATUSES = [
  NAVIGO_STATUS.COMPLETED,
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.REPORT_GENERATED,
  NAVIGO_STATUS.SUCCESSFUL_HRA,
  NAVIGO_STATUS.SUCCESSFUL_PRC,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.NO_SHOW,
];

/**
 * STATUS COLOUR, matching BRIDGE'S PRODUCTION DASHBOARD exactly, on Nittin's
 * instruction — so an operator moving between the two apps sees the same chip.
 *
 * THIS REVERSES AN EARLIER DELIBERATE DIVERGENCE. The comment that stood here
 * said phr split the cancelled family out on purpose and that "read side by
 * side, bridge is the one that looks wrong". Parity was chosen over that.
 *
 * Bridge's rule as ACTUALLY RENDERED — note NavigoDashboard.js:1814 branches on
 * isEndStatus() BEFORE the <Select>, so that Select's own className ternary at
 * :1832 has dead COMPLETED and CANCELLED arms and never colours a terminal row:
 *
 *   terminal        -> .navigo-appoint-non-select-status-closed  (:2038)
 *   Confirmed       -> .invalid-class
 *   In progress     -> .pending-class
 *   everything else -> .progress-class   <- bridge's `default:` arm, hence the
 *                                           "progress" fallback at every call site
 *
 * TWO CONSEQUENCES, both accepted deliberately:
 *
 *   · CANCELLED, DECLINED, NOT ELIGIBLE and NO SHOW wear the SAME GREEN as
 *     COMPLETED, because bridge groups on "is it closed", not "did it go well".
 *     A cancelled appointment and a completed one are the same colour.
 *   · The palette is bridge's and it is low-contrast — pending measures 1.58:1
 *     and progress 2.52:1 against their own backgrounds, against WCAG AA's
 *     4.5:1 floor. Accepted for parity.
 *
 * Bridge does ship a better partition it does not use here —
 * COMPLETED_STATUS_SET / CANCELLED_STATUS_SET (its appointmentConstants.js:178)
 * — which its newer appointments-v2 module colours from. If the green-for-
 * cancelled ever reads as a bug rather than a target, that set is the fix.
 */
export const STATUS_TONE = Object.freeze(
  Object.keys(NAVIGO_STATUS).reduce((acc, key) => {
    const code = NAVIGO_STATUS[key];
    acc[code] =
      TERMINAL_STATUSES.indexOf(code) !== -1
        ? "closed"
        : code === NAVIGO_STATUS.CONFIRMED
        ? "invalid"
        : code === NAVIGO_STATUS.IN_PROGRESS
        ? "pending"
        : "progress";
    return acc;
  }, {})
);

/**
 * The statuses the dropdown OFFERS, by who is signed in (Nittin, 2026-09-11).
 *
 * A nutritionist's or medical-team member's calls end in an HRA / PRC outcome;
 * a doctor's, physiotherapist's or psychotherapist's end in Completed. Both
 * lists are consultation-only (no LAB_WORKFLOW_STATUSES), both offer Confirmed
 * — LAST, after Cancelled (product's list, 2026-09-17) — and neither offers
 * No show. The order is the product's, not bridge's.
 *
 * Note SCHEDULED_LEGACY(0), not SCHEDULED(5): that is what bridge sends. The
 * server treats them identically ("Status 0 and 5 same in DB",
 * models/constants.go:63).
 *
 * An appointment already in a status that is not offered still shows its
 * label — StatusSelect takes the label separately from the options.
 */
export const NUTRITIONIST_STATUSES = Object.freeze([
  NAVIGO_STATUS.SCHEDULED_LEGACY,
  NAVIGO_STATUS.CALL_LATER,
  NAVIGO_STATUS.NOT_ANSWERED,
  NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
  NAVIGO_STATUS.NOT_REACHABLE,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.SUCCESSFUL_HRA,
  NAVIGO_STATUS.SUCCESSFUL_PRC,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.CONFIRMED,
]);

/** Doctors, physiotherapists and psychotherapists. (Nutritionists and the medical team: above.) */
export const CLINICIAN_STATUSES = Object.freeze([
  NAVIGO_STATUS.SCHEDULED_LEGACY,
  NAVIGO_STATUS.CALL_LATER,
  NAVIGO_STATUS.NOT_ANSWERED,
  NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
  NAVIGO_STATUS.NOT_REACHABLE,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.COMPLETED,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.CONFIRMED,
]);

/**
 * The list for a doctorProfile category (UserService.doctorCategory()): a
 * NUTRITIONIST or a MEDICAL_TEAM member gets the HRA / PRC list; a doctor,
 * physiotherapist or psychotherapist — and an unknown category (0), which the
 * product did not name — gets the clinician list.
 */
const HRA_PRC_CATEGORIES = [DoctorCategory.NUTRITIONIST, DoctorCategory.MEDICAL_TEAM];
export const selectableStatusesFor = (doctorCategory) =>
  HRA_PRC_CATEGORIES.indexOf(Number(doctorCategory)) !== -1 ? NUTRITIONIST_STATUSES : CLINICIAN_STATUSES;

/**
 * The lab / home-sample dispositions. phr shows consultation appointments only,
 * so none of these is OFFERED anywhere — not in the status dropdowns
 * (selectableStatusesFor) and not in the dashboard's Status filter
 * (appointmentFilters statusOptionsFor). Their labels and tones stay, so a row
 * that is somehow in one still reads correctly.
 */
export const LAB_WORKFLOW_STATUSES = Object.freeze([
  NAVIGO_STATUS.IN_PROGRESS,
  NAVIGO_STATUS.PHLEBO_ASSIGNED,
  NAVIGO_STATUS.PICKUP,
  NAVIGO_STATUS.SAMPLE_COLLECTED,
  NAVIGO_STATUS.SAMPLE_RECEIVED,
  NAVIGO_STATUS.REPORT_GENERATED,
  NAVIGO_STATUS.REQUEST_TO_LAB,
  NAVIGO_STATUS.LAB_VISITED,
]);

/**
 * The server's computed payment state for an appointment — NOT a stored column.
 * api-appointment-service models/appointment.go:75 tags it
 * `json:"paymentStatusResponse" bson:"-"`, and repository/appointment.go:1169+
 * derives it per row on the way out, so it is present on the filter/by-id read
 * this screen uses and absent from anything written.
 *
 * Values are bridge's (its appointmentConstants.js:90-100), including the GAP at
 * 5: LINK_NOT_GENERATED was removed there rather than renumbered, so 6 really is
 * LINK_EXPIRED and the hole must not be closed up.
 */
export const APPOINTMENT_PAYMENT_STATUS = Object.freeze({
  PENDING: 0,
  INITIATED: 1,
  COMPLETE: 2,
  FAILED: 3,
  SPONSORED: 4,
  /* 5 = LINK_NOT_GENERATED, retired */
  LINK_EXPIRED: 6,
  LINK_CANCELLED: 7,
  REFUNDED: 8,
});

/**
 * Terminal statuses that mean the appointment did NOT happen — bridge's
 * CANCELLED_STATUS_SET (appointmentConstants.js:186-191).
 *
 * The same four statuses as REASON_REQUIRED_STATUSES below, and deliberately a
 * SEPARATE list: that one decides which changes ask for a reason, this one what
 * the screen says about a cancelled appointment. Changing one rule should not
 * silently move the other.
 */
export const CANCELLED_STATUSES = Object.freeze([
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.NOT_ELIGIBLE,
  NAVIGO_STATUS.NO_SHOW,
]);

/** Cancelled/failed terminal status? (bridge's isEndStatusCancelled.) */
export const isEndStatusCancelled = (statusCode) =>
  CANCELLED_STATUSES.indexOf(statusCode) !== -1;

/**
 * Moving to one of these asks for a mandatory reason first
 * (CancelAppointmentDialog). They are also the statuses on which the server
 * considers an automatic refund (appointment_helper.go createAutoRefund's status
 * guard) — which phr leaves to the server, as the expert dashboard does; see
 * hooks/useStatusChange.
 */
export const REASON_REQUIRED_STATUSES = Object.freeze([
  NAVIGO_STATUS.CANCELLED,
  NAVIGO_STATUS.NO_SHOW,
  NAVIGO_STATUS.DECLINED,
  NAVIGO_STATUS.NOT_ELIGIBLE,
]);

/** Terminal. The server rejects cancelling out of these, so don't offer it. */
export const END_STATUSES = Object.freeze(TERMINAL_STATUSES);

export const PAGE_SIZE = 20;

/**
 * The ONE reschedule reason the dialog owns. Every other chip comes from
 * GET /v1/appointment/reschedule-reasons and is rendered verbatim — bridge
 * appends this string to that list (navigo-module DocumentUpload.js:214) and it
 * is the chip that reveals the free-text comment.
 *
 * This replaced a hardcoded four-chip list the Figma drew ("Patient requested",
 * "Patient unreachable", "Doctor unavailable", "Other") under a comment claiming
 * bridge sends no reason. Both were wrong: bridge fetches its chips from the
 * server and SENDS the picked one as `reason` on every reschedule.
 */
export const RESCHEDULE_OTHER_REASON = "Other reason";

/**
 * Booking modes and sub-types the reschedule dialog branches on — bridge's
 * navigo-module helper/appointmentConstants.js MODES / MODES_WITH_AUTO_SP /
 * SERVICE_SUB_TYPE, verbatim. The dialog compares `mode` to these EXACT
 * strings, as bridge does (===, not case-insensitive).
 */
export const MODES = Object.freeze({
  TELE_CONSULTATION: "Tele consultation",
  VIDEO_CONSULTATION: "Video consultation",
  IN_PERSON_CONSULTATION: "In person consultation",
  HOME_SAMPLE: "Home sample",
  CENTRE_VISIT: "Centre visit",
  CAMP_SITE_VISIT: "Camp site visit",
});

/**
 * The ONLY modes the appointments dashboard lists (Nittin, 2026-09-16): tele
 * and in-person consultations, sent as the filter's `serviceMode` — matched
 * EXACTLY (`mode $in`, api-appointment-service repository/appointment.go),
 * which is why these are the server's own constants (models/constants.go).
 *
 * This is also what makes the list consultations-only. `serviceTypeID: [1]`
 * would say it more directly but is not safe: an appointment's stored
 * serviceTypeID was not always written ("was not set earlier",
 * appointment_controller.go, 2025-09-03), and the list endpoint overwrites it
 * from the catalogue on read, so older consultations could be silently
 * dropped. Both modes exist only for consultation services.
 */
export const DASHBOARD_MODES = Object.freeze([
  MODES.TELE_CONSULTATION,
  MODES.IN_PERSON_CONSULTATION,
]);

/** Modes whose "select automatically" box can be ticked (Home sample is then
    excluded again by the dialog itself, exactly as in bridge). */
export const MODES_WITH_AUTO_SP = Object.freeze([
  MODES.VIDEO_CONSULTATION,
  MODES.TELE_CONSULTATION,
  MODES.HOME_SAMPLE,
]);

export const SERVICE_SUB_TYPE = Object.freeze({
  AVAIL_CARE: 3,
});

/**
 * Service-type ids (bridge SERVICE_TYPE). Only GYM matters to us — bridge blocks
 * rescheduling gym bookings (they have no slot model).
 */
export const SERVICE_TYPE = Object.freeze({
  CONSULTATION: 1,
  HEALTH_CHECKUP: 2,
  VACCINE: 3,
  GYM: 4,
  DENTAL: 5,
});

/**
 * Options the dropdown must offer but not allow, given the CURRENT status.
 *
 * Bridge disables "Scheduled" when the current status is COMPLETED or
 * RESCHEDULE_REQUESTED, and "No show" when it is RESCHEDULE_REQUESTED — on both
 * its dashboard and its details screen.
 *
 * Only the RESCHEDULE_REQUESTED half is reachable here: COMPLETED is terminal,
 * so the control is already read-only by then (END_STATUSES). Porting the dead
 * half would be copying bridge's dead code rather than its behaviour.
 *
 * This matters beyond tidiness: NO_SHOW is refund-triggering, so without it the
 * operator can reach a money path from a state bridge deliberately blocks.
 */
export const disabledStatusOptions = (currentStatus) =>
  currentStatus === NAVIGO_STATUS.RESCHEDULE_REQUESTED
    ? [NAVIGO_STATUS.SCHEDULED_LEGACY, NAVIGO_STATUS.SCHEDULED, NAVIGO_STATUS.NO_SHOW]
    : [];

/** Terminal status? (over the shared END_STATUSES — bridge's END_STATUS_SET.) */
export const isEndStatus = (statusCode) => END_STATUSES.indexOf(statusCode) !== -1;

/**
 * Why an appointment can't be rescheduled, or "" when it can — bridge's exact
 * gate (DocumentUpload.js hides the Reschedule button when any of these hold):
 * the server flagged it `blockRescheduling`, it's a gym booking, or it's in a
 * terminal status. Bridge hides the control; phr keeps phr's convention of a
 * disabled button whose tooltip says why. Null/undefined ⇒ blocked.
 */
export const rescheduleBlockReason = (appointment) => {
  if (!appointment) return "No appointment to reschedule";
  if (appointment.blockRescheduling === true) {
    return "Rescheduling is blocked for this appointment";
  }
  if (appointment.serviceTypeID === SERVICE_TYPE.GYM) {
    return "Gym sessions can't be rescheduled here";
  }
  if (isEndStatus(appointment.status)) {
    const label = STATUS_LABEL[appointment.status] || "closed";
    return `Can't reschedule — this appointment is ${label.toLowerCase()}`;
  }
  return "";
};

/** Convenience boolean over rescheduleBlockReason. */
export const canReschedule = (appointment) => !rescheduleBlockReason(appointment);

/**
 * Modes that belong to a CONSULTATION service.
 *
 * This is the SERVER's own grouping, not ours: api-appointment-service's
 * models/constants.go lists exactly these three under `// consultation modes`
 * (:212-217), against `// health checkup modes` (:205-210) for Home sample,
 * Centre visit and Camp site visit.
 */
export const CONSULTATION_MODES = Object.freeze([
  MODES.TELE_CONSULTATION,
  MODES.VIDEO_CONSULTATION,
  MODES.IN_PERSON_CONSULTATION,
]);

/**
 * Is this appointment a consultation?
 *
 * The TYPE decides when the row carries one, and the MODE only as a fallback.
 * Neither test works alone:
 *
 *   · `serviceTypeID` is authoritative but not always THERE. It was not always
 *     written ("was not set earlier", appointment_controller.go, 2025-09-03);
 *     it has no omitempty (models/appointment.go:54), so it arrives as 0 rather
 *     than absent, and only reads that enrich backfill it from the catalogue
 *     (repository/appointment.go:1033). Testing it alone silently drops older
 *     consultations — the same trap DASHBOARD_MODES exists to avoid.
 *   · mode alone would read a health checkup as a consultation whenever its
 *     mode is blank.
 *
 * With neither field it answers NO: the note is a prompt, and prompting on a
 * lab collection is the thing being removed here.
 */
export const isConsultation = (appointment) => {
  const type = Number((appointment && appointment.serviceTypeID) || 0);
  if (type) return type === SERVICE_TYPE.CONSULTATION;
  return CONSULTATION_MODES.indexOf((appointment && appointment.mode) || "") !== -1;
};

/**
 * The outcomes that open NO call note: nobody was reached, so there is no
 * conversation to write up — and these are the statuses an operator sets most
 * often in a row, which had made the note a modal to dismiss rather than a thing
 * to fill in. "Not answered" and "Not answered (3 attempts)" (Nittin,
 * 2026-09-25); "Not reachable" (Nittin, 2026-09-28), which is the same outcome
 * by a different route — the call never connected.
 *
 * A LIST, not a chain of !==, because it is now three and the next one should be
 * a one-line change at one place in each repo rather than an edit to a boolean
 * expression at the end of a file.
 */
export const NO_CALL_NOTE_STATUSES = Object.freeze([
  NAVIGO_STATUS.NOT_ANSWERED,
  NAVIGO_STATUS.NOT_ANSWERED_3_ATTEMPTS,
  NAVIGO_STATUS.NOT_REACHABLE,
]);

/**
 * Does a status change open the CALL NOTE?
 *
 * TWO rules. It takes the APPOINTMENT as well as the status so that neither can
 * be applied without the other: this predicate is the whole contract at seven
 * call sites across two repos, and the status-only version it replaces had
 * already been half-applied once (the patient screen opens its own note and was
 * missed).
 *
 *   · CONSULTATIONS ONLY (Nittin, 2026-09-28). A call note records a
 *     consultation call; a home-sample collection or a health checkup has none.
 *   · Every outcome BUT the ones in NO_CALL_NOTE_STATUSES above — the three
 *     where the call did not happen.
 *
 * The note stays reachable BY HAND in every case, from "Add call note". This is
 * about what opens ITSELF, not about what may be written.
 */
export const opensCallNote = (appointment, status) =>
  isConsultation(appointment) &&
  NO_CALL_NOTE_STATUSES.indexOf(Number(status)) === -1;
